// File: frontend/src/pages/PrivacyConsentPage.jsx
import React, { useState } from 'react'
import axios from 'axios'
import { useAuthContext } from '../contexts/AuthContext'
import { FiAlertTriangle, FiCheckCircle, FiLoader } from 'react-icons/fi'
import { LATEST_POLICY_VERSION } from '../routes/ProtecteRoutes'

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'

const PrivacyConsentPage = () => {
  const { token, checkPrivacyStatus, logout } = useAuthContext()
  const [isChecked, setIsChecked] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleAccept = async () => {
    if (!isChecked) {
      setError(
        "Devi spuntare la casella per confermare di aver letto e accettato l'informativa."
      )
      return
    }
    setIsLoading(true)
    setError('')
    try {
      await axios.post(
        `${API_BASE_URL}/users/me/accept_privacy_policy`,
        { policy_version: LATEST_POLICY_VERSION },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      // On success, trigger a re-check of the privacy status.
      // This will cause the ProtectedRoute to re-evaluate and grant access.
      await checkPrivacyStatus(token)
    } catch (err) {
      console.error('Error accepting privacy policy:', err)
      setError(err.response?.data?.detail || 'Si è verificato un errore. Riprova.')
      setIsLoading(false)
    }
    // No need to set loading to false on success, as the page will redirect.
  }

  const handleLogout = () => {
    logout()
  }

  return (
    <div className="privacy-consent-container">
      <div className="privacy-consent-box">
        <img src="/franca-logo.png" alt="F.R.A.N.C.A." className="privacy-logo" />
        <div className="privacy-policy-text-area">
          <h3 className="text-2xl font-semibold text-center mb-4">
            Regolamento per l'utilizzo di F.R.A.N.C.A. AI, la Piattaforma di
            Intelligenza Artificiale del Gruppo S.F.R.E.
          </h3>

          <p>
            Il Gruppo S.F.R.E. (di seguito S.F.R.E.) mette a disposizione dei propri
            collaboratori (i.e. dipendenti e/o consulenti) F.R.A.N.C.A. AI, una
            piattaforma di intelligenza artificiale interna (di seguito la
            ‘Piattaforma') sviluppata per supportare il personale del Gruppo nelle
            attività quotidiane e consentire un miglioramento dell'efficienza e della
            produttività.
          </p>
          <p>
            F.R.A.N.C.A. AI si basa sull'intelligenza artificiale e S.F.R.E., insieme
            agli sviluppatori, si impegna a fornire uno strumento affidabile per il
            raggiungimento degli scopi sopra riportati, ma in nessun caso può garantire
            o assicurare:
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1 my-4">
            <li>
              che la Piattaforma comprenda pienamente le richieste e fornisca
              risposte/informazioni corrette;
            </li>
            <li>
              che le informazioni fornite possano considerarsi accurate e complete;
            </li>
            <li>
              il perfetto funzionamento della Piattaforma, in quanto eventuali errori o
              carenze potrebbero verificarsi durante l'uso.
            </li>
          </ul>
          <p>
            Le informazioni fornite dalla Piattaforma non rappresentano, e non possono
            rappresentare direttamente, le posizioni/opinioni del Gruppo.
          </p>

          <p className="text-center font-bold my-4">*** *** ***</p>

          <p>
            L'accesso e l'utilizzo della Piattaforma sono soggetti al rispetto del
            presente regolamento, delle policy aziendali e della normativa vigente.
          </p>

          <section>
            <h4 className="text-xl font-bold mt-6 mb-2">1. Condizioni di Utilizzo</h4>
            <ul className="space-y-3">
              <li>
                <strong>Finalità esclusivamente lavorativa:</strong> La Piattaforma deve
                essere utilizzata esclusivamente per scopi professionali riconducibili
                alle attività prestate in favore delle società del Gruppo S.F.R.E. e,
                conseguentemente, deve considerarsi vietato qualsiasi uso per finalità
                personali. L'accesso è riservato a utenti con credenziali aziendali
                @sfre.it., da considerarsi strettamente personali e non condivisibili.
                Nessun utente esterno è autorizzato ad accedere alla piattaforma,
                neanche per interposta persona.
              </li>
              <li>
                <strong>Riservatezza delle informazioni:</strong> Qualsiasi dato
                inserito nella Piattaforma (input) e qualsiasi informazione generata
                dalla stessa (output) devono essere considerati{' '}
                <strong>dati aziendali confidenziali</strong>. È vietato divulgare,
                copiare o trasferire tali informazioni all'esterno dell'azienda o
                condividerle con personale non autorizzato.
              </li>
              <li>
                <strong>
                  Divieto di inserimento dati sensibili o personali non pertinenti:
                </strong>{' '}
                È fatto divieto di inserire nella Piattaforma dati personali di clienti,
                colleghi o terzi, o altre categorie di dati sensibili, a meno che non
                sia strettamente necessario per l'adempimento di una specifica e
                legittima attività lavorativa e in piena conformità con le normative
                sulla privacy (GDPR). In qualsiasi caso si rappresenta che la
                Piattaforma tratta esclusivamente i contenuti delle chat inseriti
                dall'utente durante l'utilizzo e, pertanto, non viene acquisito alcun
                dato sensibile, né informazioni personali non pertinenti alle finalità
                operative.
              </li>
              <li>
                <strong>Responsabilità e verifica degli Output:</strong> La Piattaforma
                deve essere considerata esclusivamente uno strumento di supporto fornito
                dal Gruppo S.F.R.E., per cui ogni collaboratore è{' '}
                <strong>personalmente responsabile</strong> della supervisione e della
                validazione dei contenuti generati dalla Piattaforma. In particolare,
                non è consentito fare affidamento esclusivo sulle informazioni fornite
                dalla Piattaforma e, viceversa, è obbligatorio verificare l'accuratezza,
                la completezza e l'adeguatezza degli output prima di ogni utilizzo,
                soprattutto se destinati ad essere inseriti in documenti ufficiali,
                comunicazioni o processi decisionali.
              </li>
              <li>
                <strong>Usi Vietati:</strong> È severamente vietato utilizzare la
                Piattaforma per:
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>
                    Generare contenuti illegali, offensivi, discriminatori, diffamatori
                    o che possano arrecare un danno, anche di immagine, alle società del
                    Gruppo S.F.R.E.
                  </li>
                  <li>Condividere le proprie credenziali di accesso con altri.</li>
                  <li>
                    Tentare di aggirare le misure di sicurezza, effettuare
                    reverse-engineering o alterare il funzionamento della Piattaforma.
                  </li>
                  <li>
                    Scaricare o trasmettere su indirizzi di posta privati o hardware
                    esterni i dati generati, in linea con il divieto generale di
                    esportazione di informazioni aziendali.
                  </li>
                </ul>
              </li>
            </ul>
          </section>

          <section>
            <h4 className="text-xl font-bold mt-6 mb-2">
              2. Sicurezza e Responsabilità dell'Utente
            </h4>
            <p>
              Tutta l'infrastruttura (hardware, software e modelli AI) è gestita
              direttamente da S.F.R.E.
            </p>
            <p>
              I dati non vengono inviati, trasferiti o condivisi con terze parti o
              fornitori esterni. Non è previsto alcun utilizzo dei dati da parte di
              sistemi di terze parti (es. cloud pubblici, API esterne o simili).
            </p>
            <p>
              La protezione delle informazioni è garantita da firewall, VPN e sistemi di
              sicurezza interni monitorati dal reparto IT.
            </p>
            <p>
              In ogni caso, l'utente è tenuto a operare nel rispetto della sicurezza
              informatica aziendale. Qualsiasi anomalia, vulnerabilità o violazione del
              presente regolamento di cui si venga a conoscenza deve essere{' '}
              <strong>segnalata senza ritardo</strong> al proprio responsabile diretto e
              al dipartimento IT.
            </p>
          </section>

          <section>
            <h4 className="text-xl font-bold mt-6 mb-2">3. Controlli e Monitoraggio</h4>
            <p>
              Non è previsto monitoraggio delle richieste da parte del personale,
              tuttavia, in linea con la normativa vigente e le policy aziendali,
              S.F.R.E. si riserva la facoltà di effettuare controlli sull'utilizzo della
              Piattaforma al fine di garantire la sicurezza, l'integrità dei sistemi
              informatici e il rispetto del presente regolamento.
            </p>
            <p>
              Qualora emergessero anomalie o indizi di violazioni, S.F.R.E. potrà
              diffondere un avviso generale per invitare al corretto utilizzo dello
              strumento.
            </p>
            <p>
              In caso di perduranti anomalie o di fondato sospetto di condotte illecite,
              S.F.R.E. si riserva di effettuare verifiche più specifiche, anche su base
              individuale, nel rispetto della normativa vigente, dei diritti dei
              lavoratori e con le finalità esclusive di tutela del patrimonio aziendale.
            </p>
          </section>

          <section>
            <h4 className="text-xl font-bold mt-6 mb-2">4. Sanzioni Disciplinari</h4>
            <p>
              La violazione delle norme contenute nel presente regolamento costituisce
              un'infrazione disciplinare e comporterà l'applicazione di{' '}
              <strong>sanzioni</strong> proporzionate alla gravità dei fatti.
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 mt-2">
              <li>
                Per consulenti e collaboratori esterni, la violazione potrà comportare
                la <strong>risoluzione del contratto</strong> e la richiesta di
                risarcimento danni.
              </li>
              <li>
                Qualora la violazione costituisca un reato, S.F.R.E. provvederà a
                informare le autorità competenti.
              </li>
              <li>
                S.F.R.E. si riserva inoltre la facoltà di{' '}
                <strong>sospendere, bloccare o limitare l'accesso</strong> alla
                Piattaforma in caso di violazione accertata, per proteggere l'integrità
                dei sistemi aziendali.
              </li>
            </ul>
          </section>

          <section>
            <h4 className="text-xl font-bold mt-6 mb-2">5. Accettazione</h4>
            <p>
              L'utilizzo della Piattaforma di Intelligenza Artificiale implica la{' '}
              <strong>piena e incondizionata accettazione</strong> di quanto stabilito
              nel presente regolamento.
            </p>
          </section>
        </div>

        {error && (
          <p className="error-message">
            <FiAlertTriangle /> {error}
          </p>
        )}

        <label className="consent-checkbox-label">
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => setIsChecked(!isChecked)}
            disabled={isLoading}
          />
          Dichiaro di aver letto e compreso l'informativa sulla privacy e acconsento al
          trattamento dei miei dati come descritto.
        </label>

        <div className="button-group">
          <button
            onClick={handleLogout}
            className="button secondary"
            disabled={isLoading}
          >
            Esci
          </button>
          <button
            onClick={handleAccept}
            className="button primary"
            disabled={!isChecked || isLoading}
          >
            {isLoading ? <FiLoader className="spinner-icon" /> : <FiCheckCircle />}
            {isLoading ? 'Accettazione in corso...' : 'Accetta e Continua'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default PrivacyConsentPage
